import React from 'react'
import styles from '../HardwareItem.module.scss'
import { FormattedDevice } from '../../../../types'
import { removeTagFromPlayer } from '../../../../../sessions/actions'
import {
  useFormattedSession,
  useTagAssignmentDragAndDrop
} from '../../../../../sessions/hooks'
import { BatteryIcon } from '../../../../../../components/Icons/BatteryIcon/BatteryIcon'
import { HardwareCommon } from '../HardwareCommon/HardwareCommon'
import { useAppDispatch } from '../../../../../../store/hooks'

export interface HardwareTagProps {
  device: FormattedDevice
  isSessionAssignment: boolean
  list?: boolean
  selectionManager?: any
}

export const HardwareTag: React.FC<HardwareTagProps> = (props) => {
  const dispatch = useAppDispatch()
  const { isSessionAssignment, device, list = false, selectionManager } = props

  const {
    onDragLeave,
    onDragOver,
    onTagDrop,
    onTagDrag,
    dropZone,
    isDragged,
    currentTagId
  } = useTagAssignmentDragAndDrop('setup')

  const { playersSessions } = useFormattedSession('setup')

  if (!isSessionAssignment) return <HardwareCommon device={device} />

  // Get player session
  const playerSession = playersSessions.byHardwareId.map[device.id]

  const handleDragStart = (e) => {
    // For list view, we don't need to check for container
    if (list) {
      onTagDrag({
        ...e,
        target: {
          ...e.target,
          id: e.target.id || device.id
        }
      })
      return
    }

    // Grid view check if the target or its ancestor is the shared container
    const isTagOrChild =
      e.target.closest(`.${styles['hardware-tag-sharedContainer']}`) !== null

    if (isTagOrChild) {
      const id = e.target.id
      onTagDrag({
        ...e,
        target: {
          ...e.target,
          id: id ? id : device.id
        }
      })
    } else {
      e.preventDefault()
    }
  }

  // Handle click for list view with selection functionality
  const handleClick = () => {
    if (selectionManager && list) {
      selectionManager.handleTagClick(device.id.toString())
    } else if (playerSession) {
      // Original functionality: if the tag is assigned to a player, remove it
      dispatch(removeTagFromPlayer(playerSession))
    }
  }

  // List view rendering
  if (list) {
    // Convert both to numbers for comparison
    // This matches the conversion done in the useSelectionAssignment hook
    const deviceIdNum =
      typeof device.id === 'string' ? parseInt(device.id) : device.id
    const selectedTagIdNum =
      selectionManager?.selectedTagId !== null
        ? typeof selectionManager.selectedTagId === 'string'
          ? parseInt(selectionManager.selectedTagId)
          : selectionManager.selectedTagId
        : null

    const isTagSelected =
      selectionManager &&
      selectedTagIdNum !== null &&
      selectedTagIdNum === deviceIdNum

    // if (isTagSelected) {
    //   console.log('====================SELECTED TAG====================', {
    //     deviceId: device.id,
    //     deviceIdType: typeof device.id,
    //     selectedTagId: selectionManager.selectedTagId,
    //     selectedTagIdType: typeof selectionManager.selectedTagId,
    //     selectedTagIdNum,
    //     deviceIdNum,
    //     match: selectedTagIdNum === deviceIdNum
    //   })
    // }

    // Check if a player is selected (player-first mode)
    const hasPlayerSelected = Boolean(
      selectionManager && selectionManager.selectedPlayerId !== null
    )

    const tagClassNames = `
      ${styles.tagListContent}
      ${playerSession ? styles.tagListLinked : ''}
      ${isTagSelected ? styles.tagSelected : ''}
      ${hasPlayerSelected && !playerSession ? styles.tagAvailableForPlayer : ''}
    `

    if (!playerSession) {
      // Tag is not assigned to a player
      return (
        <div
          className={styles.tagListContainer}
          onClick={handleClick}
          data-selected={isTagSelected ? 'true' : 'false'}
          data-tag-id={device.id}
          data-selected-tag-id={selectionManager?.selectedTagId}
        >
          <div
            id={`${device.id}`}
            style={{
              backgroundColor: device.status.colour,
              position: 'relative'
            }}
            className={tagClassNames}
          >
            <div className={styles.tagListBatteryContainer}>
              <BatteryIcon device={device} />
            </div>
            <div className={styles.tagListSerial}>{device.serial}</div>

            {isTagSelected && (
              <div className={styles.tagSelectionIndicator}>✓</div>
            )}
          </div>
        </div>
      )
    } else {
      // Tag is assigned to a player
      return (
        <div className={styles.tagListContainer} onClick={handleClick}>
          <div
            id={`${device.id}`}
            style={{ backgroundColor: device.status.colour }}
            className={tagClassNames}
          >
            <div className={styles.tagListBatteryContainer}>
              <BatteryIcon device={device} />
            </div>
            <div className={styles.tagListSerial}>{device.serial}</div>
            {playerSession.player?.abbreviatedName && (
              <div className={styles.tagListPlayerName}>
                {playerSession.player?.abbreviatedName}
              </div>
            )}
          </div>
        </div>
      )
    }
  }

  if (!playerSession) {
    if (isDragged && parseInt(currentTagId) === device.id) {
      return (
        <div
          id={`${device.id}`}
          className={`${styles['hardware-tag-container']}`}
        >
          <div
            style={{ backgroundColor: device.status.colour }}
            className={`${styles['hardware-tag-sharedContainer']}`}
            draggable={true}
            onDragStart={(e) => handleDragStart(e)}
          >
            <div className={styles['hardware-tag-encodedId']} draggable={false}>
              <div className={styles['hardware-tag-batteryContainer']}>
                <BatteryIcon device={device} />
              </div>
              {device.serial}
            </div>
          </div>
        </div>
      )
    } else {
      return (
        <div className={`${styles['hardware-tag-container']}`}>
          <div
            style={{ backgroundColor: device.status.colour }}
            className={`${styles['hardware-tag-sharedContainer']}`}
            id={`${device.id}`}
            draggable={true}
            onDragStart={(e) => handleDragStart(e)}
          >
            <div className={styles['hardware-tag-encodedId']}>
              <div className={styles['hardware-tag-batteryContainer']}>
                <BatteryIcon device={device} />
              </div>
              {device.serial}
            </div>
          </div>
        </div>
      )
    }
  } else {
    return (
      <div className={`${styles['hardware-tag-container']}`}>
        <div
          style={{ backgroundColor: device.status.colour }}
          onDragOver={(e) => onDragOver(e)}
          onDragLeave={(e) => onDragLeave(e)}
          onDrop={(e) => onTagDrop(e, playerSession.playerId, null)}
          onClick={() => dispatch(removeTagFromPlayer(playerSession))}
          className={`${styles['hardware-tag-sharedContainer']}
              ${styles['hardware-tag-linkedBorder']}
              ${dropZone === device.id && styles['hardware-tag-activeDrop']}`}
        >
          <div
            id={`${device.id}`}
            data-playerid={playerSession.playerId}
            className={`${styles['hardware-tag-onDrop']} tagDropZone`}
          ></div>
          <div className={styles['hardware-tag-encodedId']}>
            <div className={styles['hardware-tag-batteryContainer']}>
              <BatteryIcon device={device} />
            </div>
            {device.serial}
          </div>
        </div>
        <div id='player-name' className={styles['hardware-tag-playerName']}>
          <div className={styles['hardware-tag-tagInfo']}>
            {playerSession.player?.abbreviatedName}
          </div>
        </div>
      </div>
    )
  }
}
